import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Button
} from '@mui/material';
import SearchComponent from '../components/SearchComponent';
import ProductCard from '../components/ProductCard';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { getCategoryProducts } from '../api/apiClient';

const HomePage = () => {
  const navigate = useNavigate();
  
  // Устанавливаем заголовок страницы
  useDocumentTitle('Главная');
  
  const [newProducts, setNewProducts] = useState([]);
  const [loadingNewProducts, setLoadingNewProducts] = useState(true);
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [productsPerPage] = useState(20); // Показываем по 20 товаров за раз
  const [hasNextPage, setHasNextPage] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);



  // Загружаем товары новинок при загрузке страницы
  useEffect(() => {
    fetchNewProducts();
  }, []);

  // Обновляем отображаемые товары при изменении новинок
  useEffect(() => {
    setDisplayedProducts(newProducts.slice(0, productsPerPage));
    setHasNextPage(newProducts.length > productsPerPage);
  }, [newProducts, productsPerPage]);



  const loadMoreProducts = useCallback(() => {
    if (loadingMore || !hasNextPage) return;
    
    setLoadingMore(true);
    
    setTimeout(() => {
      const currentCount = displayedProducts.length;
      const nextProducts = newProducts.slice(currentCount, currentCount + productsPerPage);
      setDisplayedProducts([...displayedProducts, ...nextProducts]);
      
      // Проверяем, есть ли еще товары для загрузки
      setHasNextPage(currentCount + productsPerPage < newProducts.length);
      setLoadingMore(false);
    }, 500); // Небольшая задержка для плавности
  }, [loadingMore, hasNextPage, displayedProducts, newProducts, productsPerPage]);

  // Функция для автоматической подгрузки при прокрутке
  const handleScroll = useCallback(() => {
    if (loadingMore || !hasNextPage) return;
    
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    
    // Загружаем следующую порцию когда пользователь приближается к концу
    if (scrollTop + windowHeight >= documentHeight - 100) {
      loadMoreProducts();
    }
  }, [loadingMore, hasNextPage, loadMoreProducts]);

  // Добавляем слушатель прокрутки
  useEffect(() => {
    if (hasNextPage && !loadingMore) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [hasNextPage, loadingMore, handleScroll]);

  const fetchNewProducts = async () => {
    try {
      setLoadingNewProducts(true);
      
      const response = await getCategoryProducts(2546081);
      
      if (response.data && response.data.productGroups) {
        const products = [];
        
        response.data.productGroups.forEach(group => {
          if (group.elements) {
            group.elements.forEach(element => {
              if (element.commercialComponents) {
                element.commercialComponents.forEach(component => {
                                  // Фильтруем только товары (Product) с ценами и названиями
                if (component.type === 'Product' && component.name && component.price) {
                  // Ищем основное изображение товара
                  let mainImage = null;
                  if (component.detail?.colors && component.detail.colors.length > 0) {
                    const firstColor = component.detail.colors[0];
                    // Сначала пробуем pdpMedia (основное изображение)
                    if (firstColor.pdpMedia?.extraInfo?.deliveryUrl) {
                      mainImage = firstColor.pdpMedia.extraInfo.deliveryUrl;
                    }
                    // Если нет pdpMedia, берем первое из xmedia
                    else if (firstColor.xmedia && firstColor.xmedia.length > 0) {
                      mainImage = firstColor.xmedia[0].extraInfo?.deliveryUrl;
                    }
                  }
                  
                  // Извлекаем информацию о цветах
                  const colors = component.detail?.colors || [];
                  
                  products.push({
                    id: component.id || Math.random().toString(),
                    name: component.name,
                    price: component.price,
                    futurePrice: component.futurePrice,
                    priceCurrency: component.priceCurrency || 'RUB',
                    originalPrice: component.originalPrice,
                    originalPriceCurrency: component.originalPriceCurrency || 'KZT',
                    displayReference: component.detail?.displayReference || component.displayReference,
                    mainImage: mainImage,
                    familyName: component.familyName,
                    sectionName: component.sectionName,
                    colors: colors
                  });
                }
                });
              }
            });
          }
        });
        
        // Фильтруем только товары с изображениями и ценами
        const validProducts = products.filter(p => p.mainImage && p.price && p.name);
        
        setNewProducts(validProducts); // Показываем все валидные товары
      }
    } catch (err) {
      setNewProducts([]);
    } finally {
      setLoadingNewProducts(false);
    }
  };



  return (
    <Box className="home-page-container" sx={{ 
      maxWidth: '1200px', 
      margin: '0 auto', 
      padding: { xs: 2, sm: 3, md: 4 },
      minHeight: '100vh'
    }}>
      {/* Hero Section */}
      <Box className="hero-section" sx={{ 
        textAlign: 'center', 
        mb: { xs: 4, md: 6 },
        py: { xs: 3, md: 6 }
      }}>
        <Typography 
          className="section-title"
          variant="h2" 
          component="h1" 
          sx={{ 
            mb: { xs: 2, md: 3 }, 
            fontWeight: 300,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
          }}
        >
          Добро пожаловать в Zara в Россию
        </Typography>
        <Typography 
          variant="h5" 
          color="text.secondary" 
          sx={{ 
            mb: { xs: 3, md: 4 },
            fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
          }}
        >
          Находите товары Zara по лучшим ценам в разных странах
        </Typography>
        
        <SearchComponent />
      </Box>

      {/* New Products Section */}
      <Box sx={{ mb: { xs: 4, md: 6 } }}>
        <Typography 
          className="section-title"
          variant="h3" 
          component="h2" 
          sx={{ 
            mb: { xs: 2, md: 3 }, 
            textAlign: 'center', 
            fontWeight: 300,
            fontSize: { xs: '1.8rem', sm: '2rem', md: '2.5rem' }
          }}
        >
          Новинки
        </Typography>
        
        {loadingNewProducts ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress size={60} />
            <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
              Загружаем новинки...
            </Typography>
          </Box>
        ) : newProducts.length > 0 ? (
          <>
            <Box className="product-grid">
              {displayedProducts.map((product, index) => (
                <ProductCard
                  key={product.id || index}
                  product={product}
                  onClick={() => navigate(`/product/${product.id}`)}
                  showColors={true}
                  showFavorite={true}
                  showNewBadge={true}
                  variant="default"
                />
              ))}
            </Box>
            
            {/* Индикатор загрузки и кнопка категорий */}
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              {loadingMore && (
                <Box sx={{ mb: 3 }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Загружаем еще товары...
                  </Typography>
                </Box>
              )}
              
              {/* Кнопка для открытия меню категорий */}
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/categories')}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  mb: 2
                }}
              >
                Показать все категории
              </Button>
              
              <Typography variant="body2" color="text.secondary">
                Показано {displayedProducts.length} из {newProducts.length} товаров
              </Typography>
            </Box>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body1" color="text.secondary">
              Товары новинок временно недоступны
            </Typography>
          </Box>
        )}
      </Box>






    </Box>
  );
};

export default HomePage;
